import momentTimezone from 'moment-timezone';
import {PlatformIntl} from 'src/infra/PlatformIntl';

/**
 * PERFORMANCE: moment-timezone custa ~0,027 ms por chamada e as listas formatam duas datas por
 * linha. Para os tokens numericos abaixo o Intl com formatador reaproveitado custa ~4x menos.
 * Qualquer outro token (nome de mes, dia da semana, AM/PM) segue pelo moment.
 */
const FAST_TOKENS = {
    YYYY: 'year', MM: 'month', DD: 'day', HH: 'hour', mm: 'minute', ss: 'second',
};
const TOKEN_PATTERN = /\[([^\]]*)\]|(YYYY|MM|DD|HH|mm|ss)|([A-Za-z])|([^A-Za-z[]+)/g;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const parsedPatterns = new Map();
const cacheParsed = (format, parsed) => {
    parsedPatterns.set(format, parsed);
    return parsed;
};
const parsePattern = (format) => {
    if (parsedPatterns.has(format)) return parsedPatterns.get(format);
    const parts = [];
    let fast = true;
    for (const {0: matched, 1: literal, 2: token, 3: unknown, 4: text, index} of format.matchAll(TOKEN_PATTERN)) {
        // Token repetido ("MMMM", "YYYYY") tem outro sentido no moment: segue por ele.
        if (token && format[index + matched.length] === token[0]) return cacheParsed(format, null);
        if (token) parts.push({field: FAST_TOKENS[token]});
        else if (literal !== undefined) parts.push({text: literal});
        else if (text !== undefined) parts.push({text});
        else fast = false;
        if (unknown) break;
    }
    return cacheParsed(format, fast ? parts : null);
};

const formatters = new Map();
const getFormatter = (timezone) => {
    let formatter = formatters.get(timezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat('en-GB', {
            timeZone: timezone,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hourCycle: 'h23',
        });
        formatters.set(timezone, formatter);
    }
    return formatter;
};

const formatWithMoment = (value, timezone, format) => momentTimezone(value).tz(timezone).format(format);

/**
 * Formata uma data no fuso, com o mesmo texto do moment-timezone. Os tokens numericos
 * (YYYY MM DD HH mm ss) saem do Intl; qualquer outro token, token repetido, data sem hora, data ou
 * fuso invalido segue pelo moment.
 *
 * @param {string|number|Date} value instante
 * @param {string} timezone fuso IANA
 * @param {string} format padrao do moment
 * @return {string} data formatada
 */
export const formatDateWithPattern = (value, timezone, format) => {
    const parts = parsePattern(format);
    // Data sem hora: o Date lê como UTC e o moment como horário local; o moment decide.
    if (!parts || DATE_ONLY.test(value)) return formatWithMoment(value, timezone, format);
    try {
        const fields = {};
        getFormatter(timezone).formatToParts(new Date(value)).forEach(({type, value: text}) => {
            fields[type] = text;
        });
        return parts.map(({field, text}) => (field ? fields[field] : text)).join('');
    } catch (e) {
        // Data ou fuso invalido: o Intl lanca, o moment devolve "Invalid date" como antes.
        return formatWithMoment(value, timezone, format);
    }
};

export const formatDateTimeColumn = (col, timezone) => {
    const intl = PlatformIntl.getInstance();
    return !col ? intl.formatMessage('notAvailable') :
        formatDateWithPattern(col, timezone, intl.formatMessage('dateTimeLongFormat'));
};
